import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    NonNullableFormBuilder,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { Cursus, NomenclatureType, TypeDiplome, nomenclature } from '@app/core/models/nomenclature.model';
import { descripteursEnqueteValidators } from '@app/shared/base-form/validators-config';
import { DescripteursEnquete, TypeObjetMaquette } from '@pcscol/odf-api-v1-angular';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AbstractDescripteursForm } from '@app/shared/form-models/abstract-descripteurs-form';

export class DescripteursEnqueteForm extends AbstractDescripteursForm {
    static readonly MAX_DOMAINES_FORMATION = 5;
    readonly maxDomainesFormation = DescripteursEnqueteForm.MAX_DOMAINES_FORMATION;
    readonly domainesFormationSelectionnes$: Observable<string[]>;

    constructor(
        protected formGroup: FormGroup,
        protected readonly typeObjetMaquette: TypeObjetMaquette,
        protected readonly typesDiplomes$: Observable<TypeDiplome[]>,
        private readonly fb: FormBuilder | NonNullableFormBuilder
    ) {
        super(formGroup, typeObjetMaquette);
        this.domainesFormationSelectionnes$ = this.domainesFormationArray.valueChanges.pipe(
            startWith(this.domainesFormationArray.value),
            map(domaines => domaines.filter((code): code is string => !!code))
        );
        this.initTypeDiplomeChangesListener();
        this.disableNomenclaturesImbriqueesFormControls();
    }

    typeDiplomeNomenclaturesImbriquees$: Observable<Cursus | null | undefined>;

    private disableNomenclaturesImbriqueesFormControls() {
        this.asDescripteursSiseFormGroup.controls.cursus.disable();
        this.asDescripteursSiseFormGroup.controls.niveauFormation.disable();
        this.asDescripteursSiseFormGroup.controls.natureDiplome.disable();
    }

    private initTypeDiplomeChangesListener() {
        this.typeDiplomeNomenclaturesImbriquees$ = combineLatest([
            this.typesDiplomes$,
            this.asDescripteursSiseFormGroup.controls.typeDiplome.valueChanges.pipe(
                startWith(this.asDescripteursSiseFormGroup.controls.typeDiplome.value ?? '')
            )
        ]).pipe(
            map(([typeDiplomeNomenclature, codeTypeDiplome]) => {
                if (!codeTypeDiplome) {
                    return null;
                }
                const td = typeDiplomeNomenclature.find(t => t.code === codeTypeDiplome);
                if (td) {
                    this.asDescripteursSiseFormGroup.controls.cursus.patchValue(td.cursus!.libelleLong);
                    this.asDescripteursSiseFormGroup.controls.niveauFormation.patchValue(td.niveauFormation!.libelleLong);
                    this.asDescripteursSiseFormGroup.controls.natureDiplome.patchValue(td.nature!.libelleLong);
                } else {
                    this.asDescripteursSiseFormGroup.controls.cursus.reset();
                    this.asDescripteursSiseFormGroup.controls.niveauFormation.reset();
                    this.asDescripteursSiseFormGroup.controls.natureDiplome.reset();
                }
                return td?.cursus;
            })
        );
    }

    public static createForm(
        fb: FormBuilder | NonNullableFormBuilder,
        typeObjetMaquette: TypeObjetMaquette,
        typesDiplomes$: Observable<TypeDiplome[]>,
        descripteursEnquete?: DescripteursEnquete
    ) {
        const form = fb.group({
            descripteursSise: fb.group({
                // descripteursOM
                codeDiplomeSise: fb.control<string | null>(null),
                codeDiplomeIntermediaireSise: fb.control<string | null>(null),
                niveauDiplomeSise: fb.control<string | null>(null, {
                    validators: descripteursEnqueteValidators.descripteursSise.niveauDiplomeSise
                }),
                parcoursTypeSise: fb.control<string | null>(null),

                typeDiplome: fb.control<string | null>(null, []),

                cursus: fb.control<string | null>(null), // fake, juste pour affichage
                niveauFormation: fb.control<string | null>(null), // fake, juste pour affichage
                natureDiplome: fb.control<string | null>(null), // fake, juste pour affichage

                domainesFormation: fb.array<string>([], { validators: [domainesFormationValidator()] }),
                mention: fb.control<string | null>(null),
                declinaisonDiplome: fb.control<string | null>(null, {
                    validators: [Validators.maxLength(150)]
                }),
                champFormation: fb.control<string | null>(null),
                niveauDiplome: fb.control<string | null>(null),
                codeLangueSise: fb.control<string | null>(null, [Validators.pattern(/^[0-9]{4}$/)])
            }),
            descripteursAglae: fb.group({
                habilitePourBoursesAglae: fb.control<boolean>(false),
                niveauAglae: fb.control<string | null>(null, { validators: descripteursEnqueteValidators.descripteursAglae.niveauAglae })
            }),
            descripteursFresq: fb.group({
                numeroFresqNiveau1: fb.control<string | null>(null, {
                    validators: descripteursEnqueteValidators.descripteursFresq.numeroFresqNiveau1
                }),
                numeroFresqNiveau2: fb.control<string | null>(null, {
                    validators: descripteursEnqueteValidators.descripteursFresq.numeroFresqNiveau2
                })
            })
        });
        const domainesFormationArray = form.controls.descripteursSise.controls.domainesFormation as FormArray<FormControl<string | null>>;
        const domainesFormation = Array.from(descripteursEnquete?.descripteursSise?.domainesFormation ?? []);
        if (domainesFormation.length) {
            domainesFormation.forEach(domaine => domainesFormationArray.push(fb.control(domaine.code)));
        } else {
            domainesFormationArray.push(fb.control<string | null>(null));
        }
        if (descripteursEnquete) {
            form.patchValue({
                descripteursSise: {
                    typeDiplome: descripteursEnquete.descripteursSise?.typeDiplome?.code || null,
                    codeDiplomeSise: descripteursEnquete.descripteursSise?.codeDiplomeSise?.code || null,
                    codeDiplomeIntermediaireSise: descripteursEnquete.descripteursSise?.codeDiplomeIntermediaireSise?.code ?? null,
                    niveauDiplomeSise: descripteursEnquete.descripteursSise?.niveauDiplomeSise,
                    mention: descripteursEnquete.descripteursSise?.mention?.code || null,
                    declinaisonDiplome: descripteursEnquete.descripteursSise?.declinaisonDiplome || null,
                    champFormation: descripteursEnquete.descripteursSise?.champFormation?.code || null,
                    niveauDiplome: descripteursEnquete.descripteursSise?.niveauDiplome?.code || null,
                    parcoursTypeSise: descripteursEnquete.descripteursSise?.parcoursTypeSise?.code,
                    codeLangueSise: descripteursEnquete.descripteursSise?.codeLangueSise || null
                },
                descripteursAglae: {
                    habilitePourBoursesAglae: descripteursEnquete.descripteursAglae?.habilitePourBoursesAglae,
                    niveauAglae: descripteursEnquete.descripteursAglae?.niveauAglae || null
                },
                descripteursFresq: {
                    numeroFresqNiveau1: descripteursEnquete.descripteursFresq?.numeroFresqNiveau1,
                    numeroFresqNiveau2: descripteursEnquete.descripteursFresq?.numeroFresqNiveau2
                }
            });
        }
        return new DescripteursEnqueteForm(form, typeObjetMaquette, typesDiplomes$, fb);
    }

    public get asDescripteursEnquete(): DescripteursEnquete {
        return {
            descripteursSise: {
                ...this.formGroup.value.descripteursSise,
                codeDiplomeSise: nomenclature(this.formGroup.value.descripteursSise.codeDiplomeSise, NomenclatureType.DiplomeSise) || null,
                codeDiplomeIntermediaireSise:
                    nomenclature(this.formGroup.value.descripteursSise.codeDiplomeIntermediaireSise, NomenclatureType.DiplomeSise) || null,
                parcoursType: nomenclature(this.formGroup.value.descripteursSise.parcoursType, NomenclatureType.ParcoursTypeSise) || null,
                niveauDiplomeSise: this.formGroup.value.descripteursSise.niveauDiplomeSise || null,
                parcoursTypeSise:
                    nomenclature(this.formGroup.value.descripteursSise.parcoursTypeSise, NomenclatureType.ParcoursTypeSise) || null,
                domainesFormation: new Set(
                    this.formGroup.value.descripteursSise.domainesFormation
                        .filter((code): code is string => !!code)
                        .map(code => nomenclature(code, NomenclatureType.DomaineFormation))
                ),
                mention: nomenclature(this.formGroup.value.descripteursSise.mention, NomenclatureType.MentionDiplome) || null,
                declinaisonDiplome: this.formGroup.value.descripteursSise.declinaisonDiplome || null,
                champFormation:
                    nomenclature(this.formGroup.value.descripteursSise.champFormation, NomenclatureType.ChampsFormation) || null,
                niveauDiplome: nomenclature(this.formGroup.value.descripteursSise.niveauDiplome, NomenclatureType.NiveauDiplome) || null,
                codeLangueSise: this.formGroup.value.descripteursSise.codeLangueSise || null,
                typeDiplome: nomenclature(this.formGroup.value.descripteursSise.typeDiplome, NomenclatureType.TypeDiplome)
            },
            descripteursAglae: {
                ...this.formGroup.value.descripteursAglae
            },
            descripteursFresq: {
                ...this.formGroup.value.descripteursFresq
            }
        };
    }

    get asDescripteursSiseFormGroup() {
        return this.formGroup.controls.descripteursSise as FormGroup;
    }

    get asDescripteursAglaeFormGroup() {
        return this.formGroup.controls.descripteursAglae as FormGroup;
    }

    get domainesFormationArray(): FormArray<FormControl<string | null>> {
        return this.asDescripteursSiseFormGroup.controls.domainesFormation as FormArray<FormControl<string | null>>;
    }

    get canAddDomaineFormation(): boolean {
        return (
            this.domainesFormationArray.length < this.maxDomainesFormation &&
            this.domainesFormationArray.controls.every(control => !!control.value)
        );
    }

    addDomaineFormation(): void {
        if (this.canAddDomaineFormation) {
            this.domainesFormationArray.push(this.fb.control<string | null>(null));
            this.domainesFormationArray.markAsDirty();
        }
    }

    removeDomaineFormation(index: number): void {
        this.domainesFormationArray.removeAt(index);
        if (!this.domainesFormationArray.length) {
            this.domainesFormationArray.push(this.fb.control<string | null>(null));
        }
        this.domainesFormationArray.markAsDirty();
    }

    get asDescripteursFresqFormGroup() {
        return this.formGroup.controls.descripteursFresq as FormGroup;
    }

    get numeroFresqNiveau1Control() {
        return this.asDescripteursFresqFormGroup.controls.numeroFresqNiveau1 as FormControl;
    }
    get numeroFresqNiveau2Control() {
        return this.asDescripteursFresqFormGroup.controls.numeroFresqNiveau2 as FormControl;
    }
    get codeLangueSiseControl() {
        return this.asDescripteursSiseFormGroup.controls.codeLangueSise as FormControl;
    }
}

function domainesFormationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const domaines = (control.value as Array<string | null>).filter((code): code is string => !!code);
        if (domaines.length > DescripteursEnqueteForm.MAX_DOMAINES_FORMATION) {
            return { maxDomainesFormation: true };
        }
        return new Set(domaines).size === domaines.length ? null : { duplicateDomainesFormation: true };
    };
}
